import React, { FC, useEffect, useState } from 'react';
import { WebhooksTableRow } from '../../../components/developers/webhooks_page/WebhooksTableRow';
import { TableComponent } from '../../../components/TableComponent';
import { useTranslation } from 'react-i18next';
import { COLUMNS } from '../../developers/WebhooksList';
import { useNavigate, useParams } from 'react-router-dom';
import { IModalWindow, ModalWindow } from '../../../components/modal/ModalWindow';
import { useLocation } from 'react-router';
import { fetchWebhooksList } from '../../../api/webhook-api-service';
import { deleteWebhook } from '../../../store/merchnts/actions/webhooks';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { HmacSigningKey } from '../../../components/developers/HmacSigningKey';

export const MerchantWebhooksListPage: FC = () => {
	const { t } = useTranslation();
	const { pathname, state } = useLocation();
	const { id: mid, partnerId } = useParams();
	const navigate = useNavigate();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const isAdmin = checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN);
	const [loading, setLoading] = useState(false);
	const [webhooks, setWebhooks] = useState([]);
	const [subscriberKey, setSubscriberKey] = useState<string>('-');
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const refetchData = async (): Promise<void> => {
		try {
			setLoading(true);

			const res = await fetchWebhooksList(mid, partnerId);
			setWebhooks(res);
			if (res?.length && res?.[0]?.subscriberKey) {
				setSubscriberKey(res?.[0]?.subscriberKey);
			}
			setLoading(false);
		} catch (e) {
			console.log(e);
			setLoading(false);
		}
	};

	useEffect(() => {
		void refetchData();
	}, []);

	const onCloseModal = (): void => setModalState(prevState => ({ ...prevState, isOpen: false }));

	const onDelete = (webhookId: string): void => {
		if (webhookId) {
			setModalState({
				isOpen: true,
				modalTitle: t('WEBHOOKS.DELETE_TITLE'),
				modalText: t('WEBHOOKS.DELETE_TEXT'),
				onConfirm: async () => {
					await deleteWebhook(webhookId, mid, partnerId);
					onCloseModal();
					await refetchData();
				},
				onCancel: onCloseModal,
				onClose: onCloseModal,
				backdrop: 'static',
			});

		}
	};

	return (
		<>
			{
				checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
					<HmacSigningKey subscriberKey={subscriberKey} loading={loading} />
				)
			}

			<div className="card">
				<TableComponent
					column={COLUMNS.filter(col => checkRoles(authUser, col.claims))}
					tableData={webhooks}
					isDataLoading={loading}
					title={t('WEBHOOKS.ENDPOINTS')}
					buttonName={isAdmin && t('WEBHOOKS.BUTTON_TEXT')}
					onButtonClick={(): void => navigate(`${pathname}/create-endpoint`, { state: { pageNumber: state?.pageNumber, pageSize: state?.pageSize }})}
					getRowComponent={(itemProps): JSX.Element => (
						<WebhooksTableRow
							{...itemProps}
							onDeleteClick={(): void => onDelete(itemProps.id)}
							key={itemProps.id}
						/>
					)}
				/>
				<ModalWindow
					{...modalState}
					onClose={onCloseModal}
				/>
			</div>
		</>
	);
};
